import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { ViewDidEnter, ViewWillLeave } from '@ionic/angular';

import { ApplePayPlugin } from '../../core/services/native-plugins/apple-pay.plugin';

import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PayOrderStatus } from '../../core/enums/pay-order-status.enum';
import { PaymentMethod } from '../../core/enums/payment-method.enum';

import { OrderPaymentOptions } from '../../core/models/order-payment-options.model';
import { PayOrderResult } from '../../core/models/pay-order-result.model';

import { CartService } from '../../core/services/cart.service';
import { LoggerService } from '../../core/services/logger.service';
import { ModalService } from '../../core/services/modal.service';
import { OverlayService } from '../../core/services/overlay.service';
import { SettingsService } from '../../core/services/settings.service';
import { PlatformService } from '../../core/services/ssr/platform.service';
import { ValidationService } from '../../core/services/validation.service';

import { BACK_BUTTON_HANDLER_PRIORITY } from '../../core/config/app.config';

import { PaymentComponent } from '../../shared/components/payment/payment.component';

export const OrderPaymentModalPageIdentifier = 'order-payment-modal';

export interface OrderPaymentModalResult {
  isSuccess: boolean;
  reason?: string;
}

@Component({
  selector: 'app-order-payment-modal',
  templateUrl: './order-payment-modal.page.html',
  styleUrls: ['./order-payment-modal.page.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OrderPaymentModalPage implements OnInit, ViewDidEnter, ViewWillLeave, OnDestroy {
  @Input()
  public orderId: number;

  @Input()
  public orderPaymentOptions: OrderPaymentOptions;

  @ViewChild(PaymentComponent)
  public payment: PaymentComponent;

  private unregisterBackButtonSubscription: Subscription;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private modalService: ModalService,
    private overlayService: OverlayService,
    private settingsService: SettingsService,
    private platformService: PlatformService,
    private translateService: TranslateService,
    private cartService: CartService,
    private validationService: ValidationService,
    private loggerService: LoggerService,
    private applePayPlugin: ApplePayPlugin,
  ) {}

  ngOnInit() {
    this.cartService.setSelectedPaymentClient(this.orderPaymentOptions.paymentOptions[0]);
  }

  ionViewDidEnter() {
    this.unregisterBackButtonSubscription = this.platformService.backButton.subscribeWithPriority(BACK_BUTTON_HANDLER_PRIORITY, () =>
      this.dismiss(null),
    );
  }

  ionViewWillLeave() {
    this.unregisterBackButtonSubscription?.unsubscribe();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  public onCancel(): void {
    void this.dismiss(null);
  }

  public onPay(): void {
    void this.overlayService.showLoading().then(() => {
      const selectedPaymentClient = this.cartService.getSelectedPaymentClient();

      const shouldUseNativeApplePay =
        selectedPaymentClient.paymentMethod === PaymentMethod.ApplePay && this.settingsService.isApplePayNativeExperienceFeatureEnabled();

      const maybeGetApplePayToken = shouldUseNativeApplePay
        ? this.applePayPlugin.getApplePayToken({
            items: [{ label: this.translateService.instant('BILBAYT') as string, amount: this.orderPaymentOptions.totalDue }],
          })
        : Promise.resolve('');

      void maybeGetApplePayToken.then((applePayTokenBase64) => {
        if (shouldUseNativeApplePay && !applePayTokenBase64) {
          this.applePayPlugin.completeLastTransaction('failure');

          void this.overlayService.hideLoading().then(() =>
            this.dismiss({
              isSuccess: false,
              reason: this.translateService.instant('PAYMENT_ERROR_MESSAGE') as string,
            }),
          );

          return;
        }

        void this.cartService
          .makeOrderPayment({
            orderId: this.orderId,
            payment: selectedPaymentClient,
            applePayTokenBase64,
          })
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe({
            next: (response: PayOrderResult) => {
              void this.overlayService.hideLoading().then(() => {
                if (shouldUseNativeApplePay) {
                  this.applePayPlugin.completeLastTransaction(response.status === PayOrderStatus.Success ? 'success' : 'failure');
                }

                if (response.status !== PayOrderStatus.Success) {
                  void this.dismiss({
                    isSuccess: false,
                    reason: response.message || (this.translateService.instant('PAYMENT_ERROR_MESSAGE_CARD_DECLINED') as string),
                  });
                  return;
                }

                if (!response.paymentUrl) {
                  void this.dismiss({ isSuccess: true });
                  return;
                }

                this.cartService
                  .handleTapPayment(response.paymentUrl)
                  .pipe(takeUntil(this.unsubscribe$))
                  .subscribe({
                    next: (result) => {
                      if (result === 'success') {
                        void this.dismiss({ isSuccess: true });
                        return;
                      }

                      if (result === 'error') {
                        void this.dismiss({
                          isSuccess: false,
                          reason: this.translateService.instant('PAYMENT_ERROR_MESSAGE_CARD_DECLINED') as string,
                        });
                        return;
                      }
                    },
                    error: () => {
                      void this.dismiss({
                        isSuccess: false,
                        reason: this.translateService.instant('PAYMENT_ERROR_MESSAGE_CARD_DECLINED') as string,
                      });
                    },
                  });
              });
            },
            error: (error: HttpErrorResponse) => {
              const errorMessage = this.validationService.getErrorMessage(error);

              this.loggerService.error({
                component: 'OrderPaymentModalPage',
                message: `couldn't make cash order payment for order ${this.orderId}`,
                error,
              });

              this.applePayPlugin.completeLastTransaction('failure');

              void this.overlayService.hideLoading();
              void this.dismiss({
                isSuccess: false,
                reason:
                  (error?.error as ErrorEvent)?.message ||
                  errorMessage ||
                  (this.translateService.instant('PAYMENT_ERROR_MESSAGE_CARD_DECLINED') as string),
              });
            },
          });
      });
    });
  }

  private dismiss(data: OrderPaymentModalResult): Promise<boolean> {
    return this.modalService.dismissModal<OrderPaymentModalResult>({
      id: OrderPaymentModalPageIdentifier,
      data,
    });
  }
}
